import { Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Linkify from 'react-linkify';
import { useVisibilityChange } from '@uidotdev/usehooks';
import { ReqUser } from './ReqUser';
import { formatDateTime } from '../../../util/dates';
import { FormField } from '../../common/Forms';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { requestsActions } from '../../../actions/requests.actions';
import { isCommentReaded } from '../../../util';

const commentField = {
    id: 'comment',
    name: '',
    inputType: 'textarea',
};

const componentDecoratorWithTargetBlank = (decoratedHref, decoratedText, key) => {
    return (
        <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer">
            {decoratedText}
        </a>
    );
};

function CommentText(props) {
    const { text = '' } = props;
    return (
        <Linkify componentDecorator={componentDecoratorWithTargetBlank}>
            {text
                .replace(/@{[^}]+}/, '')
                .split('\n')
                .filter((textLine) => textLine.trim() !== '')
                .map((textLine, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={i}>
                        {textLine}
                        <br />
                    </React.Fragment>
                ))}
        </Linkify>
    );
}

function OneRequestComment({ comment }) {
    const dispatch = useDispatch();
    const { ref, inView } = useInView({
        threshold: 0.3,
    });
    const selfId = useSelector((state) => state.auth && state.auth.user && state.auth.user.id);
    const documentVisible = useVisibilityChange();
    const readCommentTimerRef = useRef(null);
    const isReaded = isCommentReaded(comment, selfId);

    const { commentBgClass, isRemark, remarkBadge, remarkText, commentDate } = useMemo(() => {
        const commentDate = ' ' + formatDateTime(comment.date).replace(' ', '\u00A0');
        if (comment.src === 'ADMINKA_REMARK') {
            return {
                commentBgClass: comment.resolved ? 'alert-success' : 'alert-danger',
                isRemark: true,
                remarkBadge: comment.resolved ? 'success' : 'danger',
                remarkText: comment.resolved ? 'Поправлено' : 'Замечание',
                commentDate,
            };
        }
        return { commentDate };
    }, [comment]);

    useEffect(() => {
        if (isReaded) {
            if (readCommentTimerRef.current) {
                clearTimeout(readCommentTimerRef.current);
                readCommentTimerRef.current = null;
            }
            return;
        }
        if (inView && documentVisible) {
            readCommentTimerRef.current = setTimeout(() => {
                console.log('send read comment');
                dispatch(requestsActions.readComment(comment.id));
                readCommentTimerRef.current = null;
            }, 2000);
        } else {
            if (readCommentTimerRef.current) {
                clearTimeout(readCommentTimerRef.current);
                readCommentTimerRef.current = null;
            }
        }
    }, [inView, comment, dispatch, isReaded, readCommentTimerRef, documentVisible]);
    return (
        <Card
            key={comment.id}
            border={comment.src === 'ADMINKA' ? 'primary' : undefined}
            bg="light"
            className="mx-auto my-3 rounded-lg"
            ref={ref}
        >
            <Card.Body className={'pt-1 ' + commentBgClass}>
                <div className="d-flex justify-content-between">
                    <p className="small">
                        <span className="font-weight-bolder mr-3">
                            <ReqUser user={comment.user} />
                        </span>
                        {commentDate}
                    </p>
                    {isRemark && (
                        <div>
                            <Badge variant={remarkBadge}>{remarkText}</Badge>
                        </div>
                    )}
                </div>
                <div className={isReaded ? '' : 'font-weight-bold'}>
                    <CommentText text={comment.text} />
                </div>
            </Card.Body>
        </Card>
    );
}

export function RequestComments(props) {
    const { request, executing, addComment } = props;
    const [commentText, setCommentText] = useState('');
    const [addStarting, setAddStarting] = useState(false);
    const disabledButton = executing || commentText.trim() === '';

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (executing) return;
            addComment({ requestId: request.id, comment: commentText.trim() });
            setAddStarting(true);
        },
        [addComment, commentText, executing, request]
    );
    const handleCommentChange = useCallback((value) => {
        setCommentText(value);
    }, []);

    const requestComments = useMemo(() => {
        if (!request || !request.comments) return [];
        return request.comments.filter((c) => !c.serviceMsg);
    }, [request]);

    useEffect(() => {
        if (executing) return;
        if (addStarting) {
            setAddStarting(false);
            setCommentText('');
        }
    }, [addStarting, request, executing]);
    return (
        <>
            <h5 id="comments">Комментарии</h5>
            <Form onSubmit={handleSubmit} noValidate>
                <Form.Group>
                    <FormField value={commentText} field={commentField} onChange={handleCommentChange} />
                </Form.Group>
                <Form.Group className="mt-3 text-right">
                    <Button variant="outline-primary" type="submit" className="px-4" disabled={disabledButton}>
                        {executing && (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        )}
                        <span>Добавить комментарий</span>
                    </Button>
                </Form.Group>
            </Form>
            {requestComments.map((comment) => (
                <OneRequestComment comment={comment} key={comment.id} />
            ))}
        </>
    );
}
